import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { FormGroup } from "@angular/forms";
import { Observable, Subscription, BehaviorSubject } from "rxjs";
import { map, tap } from "rxjs/operators";
import { Organization } from '../../models/organization.model';
import { Well } from "../../models/well.model";
import { Contact } from "../../../admin/models/contact.model";
import { NoteRelatedItemType } from "../../models/note_relatedItemType.enum";
import { NoteType } from "../../models/note-type.enum";
import { Note } from "../../models/note.model";
import { RetentionPolicy } from "../../models/retention-policy.model";
import { NoteService } from "../../services/note.service";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { CriticalNoteService } from "../../services/critical-note.service";
import { genId } from "../../../core/services/genId";
import { AuthService } from "../../../services/auth.service";
import { UserIdentity } from "../../../models/user-identity.model";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { LoaderService } from "../../services/loader.service";

@Component({
    selector: 'critical-notes',
    templateUrl: './critical-notes.component.html',
    styleUrls: ['./critical-notes.component.scss'],
    standalone: false
})
export class CriticalNotesComponent implements OnInit, OnDestroy {

    @Input() contact: Contact;

    confirmDeleteDialog: Dialog = new Dialog("This will mark the note as deleted.  The note will no longer be visible in any view.  Do you wish to continue?", [new DialogButton("NO", false, () => { return this.cancelDelete(); }), new DialogButton("Yes", true, () => { this.confirmDelete(); })]);
    confirmDemoteDialog: Dialog = new Dialog("This will change the note to private.  The note will still be visible in the Notes and History sidebar, but will no longer show in the 'Critical Notes' at the top of the details page.  Do you wish to continue?", [new DialogButton("NO", false, () => { return this.cancelDemote(); }), new DialogButton("Yes", true, () => { this.confirmDemote(); })]);

    currentMode: CriticalNotesMode = CriticalNotesMode.SummaryView;

    get firstCriticalNote(): Note {
        if (this.notes == null || this.notes.length === 0) return null;
        return this.notes[0];
    }

    get firstNoteBrief(): string {
        if (this.firstCriticalNote == null) return this.noNoteText;
        let text: string = this.firstCriticalNote.text;
        if (text.length > this.maxLength) text = `${text.substring(0, this.maxLength - 1)}...`;
        return text;
    }

    private _froalaOptions: any = null;
    get froalaOptions(): any {
        if (!this._froalaOptions) {
            let conf = (window as any)["PdfConfig"] as any;
            let key = (conf != null) ? conf.editorLicenseKey : "";
            this._froalaOptions = {
                heightMax: 500,
                height: 500,
                heightMin: 150,
                key: key,
                typingTimer: 150,
                saveInterval: 0,
                toolbarButtons: ['bold', 'italic', 'underline', 'strikeThrough']
            };
        }
        return this._froalaOptions;
    };

    get isSaving(): boolean {
        return this.saving$.value;
    }

    maxLength: number = 150;

    modes = CriticalNotesMode;

    noteForm: FormGroup = new FormGroup({});

    notes: Note[] = [];

    noNoteText: string = "Organization has no critical notes.";

    @Input() organization: Organization;

    get relation(): string {
        return NoteRelatedItemType[this.relationType];
    }

    @Input() relationType: NoteRelatedItemType;

    saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    savingObs$: Observable<boolean> = this.saving$.asObservable();

    private subscription: Subscription = new Subscription();

    toBeDeletedId: string;
    toBeDemotedId: string;

    @Input() well: Well;

    get user(): UserIdentity {
        return this.auth.User;
    }


    constructor(private noteService: NoteService, private dialogService: DynamicDialogService, private criticalNoteService: CriticalNoteService, private auth: AuthService, private loader: LoaderService) { }

    ngOnInit() {
        let obs$: Observable<Note[]> = this.noteService.getNotes(this.organization.id, NoteRelatedItemType.Organization);

        let subs$ = obs$
            .pipe(
                map((notes: Note[]) => { return notes.filter(n => n.type === NoteType.Critical); }))
            .subscribe(notes => {
                this.notes = notes.sort((a, b) => {
                    if (a.createdDate < b.createdDate) return 1;
                    if (a.createdDate > b.createdDate) return -1;
                    return 0;
                });
        });
        this.subscription.add(subs$);

        let added$: Observable<Note> = this.criticalNoteService.noteAddedObs$.pipe(
            tap((note: Note) => { this.notes.unshift(note); })
        );

        let add$: Subscription = added$.subscribe(
            () => { },
            (error) => {
                this.handleError(error);
            }
        );
        this.subscription.add(add$);
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    editorReady: boolean = false;
    isDialogOpen: boolean = false;

    addCriticalNote(): void {
        this.currentMode = CriticalNotesMode.InputView;
        this.isDialogOpen = true;
        if (this.editorReady) {
            setTimeout(() => {
                this.noteForm.reset();
                this.noteForm.updateValueAndValidity();
            }, 0);
        }
    }

    cancelDelete(): boolean {
        this.toBeDeletedId = null;
        return false;
    }

    cancelDemote(): boolean {
        this.toBeDemotedId = null;
        return false;
    }

    cancelNote(closeEditor: boolean): void {
        if (closeEditor) {
            this.currentMode = CriticalNotesMode.LongView;
        } else {
            this.noteForm.reset();
            this.noteForm.updateValueAndValidity();
        }
    }

    closeDialog(): void {
        this.isDialogOpen = false;
        setTimeout(() => {
            this.currentMode = CriticalNotesMode.SummaryView;
        }, 400);
    }

    confirmDelete(): void {
        const toBeDeleted: number = this.notes.findIndex(n => n.id === this.toBeDeletedId);
        if (toBeDeleted !== -1) {
            let obs$: Observable<Note> = this.criticalNoteService.deleteNote(this.toBeDeletedId, this.relationType);
            this.loader.toggleLoader(true);
            let subs$ = obs$.pipe()
                .subscribe(
                    (note: Note) => {
                        this.notes.splice(toBeDeleted, 1);
                        this.loader.toggleLoader(false);
                        if (this.notes.length === 0) this.closeDialog();
                    }
                );
            this.subscription.add(subs$);
        } else {
            this.handleError(`Could not find note ${this.toBeDeletedId} to delete`);
        }
    }

    confirmDemote(): void {
        const toBeDemoted: number = this.notes.findIndex(n => n.id === this.toBeDemotedId);
        if (toBeDemoted != -1) {
            let obs$: Observable<Note> = this.criticalNoteService.demoteNote(this.toBeDemotedId, NoteType.Internal, this.relationType);
            this.loader.toggleLoader(true);
            let subs$ = obs$.pipe()
                .subscribe(
                    (note: Note) => {
                        this.notes.splice(toBeDemoted, 1);
                        this.loader.toggleLoader(false);
                        if (this.notes.length === 0) this.closeDialog();
                    },
                    (error) => {
                        this.handleError(error);
                    }
            );
            this.subscription.add(subs$);
        } else {
            this.handleError(`Could not find note ${this.toBeDemotedId} to demote`);
        }
    }

    deleteNote(id: string): void {
        this.toBeDeletedId = id;
        this.confirmDeleteDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.confirmDeleteDialog);
    }

    demoteNote(id: string): void {
        this.toBeDemotedId = id;
        this.confirmDemoteDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.confirmDemoteDialog);
    }

    editorFormBuilt(evt: FormGroup): void {
        this.noteForm = evt;
        this.editorReady = true;
    }

    handleError(error: any): void {
        console.error(error);
        this.saving$.next(false);
        this.loader.toggleLoader(false);
        this.dialogService.showError();
    }

    saveNote(): void {
        if (this.noteForm.valid) {
            const id: any = genId();
            let note: Note = {
                id: id,
                relatedItemType: this.relationType,
                createdDate: new Date().toUTCString(),
                createdById: this.auth.User.id,
                createdBy: this.auth.User.displayName,
                isDeleted: false,
                retentionPolicy: {} as RetentionPolicy,
                type: NoteType.Critical,
                priority: 3,
                noteUrgency: 0,
                noteId: id,
                text: this.noteForm.get('content').value
            } as Note;
            switch (this.relationType) {
                case NoteRelatedItemType.Organization:
                    note.relatedItemId = this.organization.id;
                    break;
                case NoteRelatedItemType.Well:
                    note.relatedItemId = this.well.id;
                    break;
                case NoteRelatedItemType.Contact:
                    note.relatedItemId = this.contact.id;
                    break;
            }
            let obs$: Observable<Note> = this.criticalNoteService.saveNote(note).pipe();
            this.saving$.next(true);
            let subs$: Subscription = obs$.subscribe(
                () => {
                    this.saving$.next(false);
                    this.noteForm.reset();
                    this.noteForm.updateValueAndValidity();
                },
                (error) => {
                    this.handleError(error);
                }
            );
            this.subscription.add(subs$);
        }

    }

    viewNotes(): void {
        this.currentMode = CriticalNotesMode.LongView;
        this.isDialogOpen = true;
    }
}

enum CriticalNotesMode {
    SummaryView,
    LongView,
    InputView
}
